import { LanguageCode, SiteTranslations } from './translation.types';

export const TRANSLATIONS: Record<LanguageCode, SiteTranslations> = {
  en: {
    meta: {
      title: 'Pupil Lab - Global Leader in Eye Tracking Technology',
    },
    header: {
      subscribe: 'Subscribe',
      support: 'Support',
      corporate: 'Corporate',
      nav: {
        products: 'Products & Solutions',
        applications: 'Applications',
        research: 'Research Consultancy',
        resources: 'Resources',
        company: 'Company',
      },
      contactUs: 'Contact us',
      menu: 'Menu',
      closeMenu: 'Close menu',
      about: 'About us',
    },
    hero: {
      slides: [
        {
          tag: 'New!',
          title: 'Webcam Eye Tracking',
          description:
            'Webcam eye tracking gives researchers a more accessible way to run eye tracking studies using standard webcams. Flexible eye tracking for on-site and remote studies.',
          ctaLabel: 'Learn More',
        },
        {
          tag: 'Customer Story',
          title: 'Improving Performance with Eye Tracking',
          description:
            'Discover how leading companies used our eye tracking technology to study behavior patterns and improve product performance through vision science.',
          ctaLabel: 'Read the Story',
        },
        {
          tag: 'Pupil Lab and AI',
          title: 'Eye Tracking Brings Contextual Awareness to AI',
          description:
            'AI lies at the heart of what we do. Our technology translates head and eye movements into insights that reveal real human behavior.',
          ctaLabel: 'Explore AI Solutions',
        },
        {
          tag: 'Pupil Lab Insight',
          title: 'Need Help Getting Started?',
          description:
            'Partner with us through tailored research services, expert consulting, or full-scale studies — designed to turn eye tracking data into meaningful results.',
          ctaLabel: 'Get Insight',
        },
        {
          tag: 'Pupil Lab Blog',
          title: 'Visual Insights for Next-Gen Applications',
          description:
            'Read the latest research on how eye tracking and visual insights are transforming industries from sports performance to healthcare diagnostics.',
          ctaLabel: 'Read the Blog',
        },
      ],
    },
    stats: {
      items: [
        { value: '26', suffix: '', label: 'Years of experience' },
        { value: '95', suffix: '', label: 'Countries worldwide' },
        { value: '1000', suffix: '+', label: 'Patents & applications' },
        { value: '15000', suffix: '+', label: 'Scientific publications' },
        { value: '5', suffix: '', label: 'Leading XR headsets powered by Pupil Lab eye tracking' },
        { value: '1', suffix: ' Million +', label: 'Cars on the road with Pupil Lab technology' },
      ],
    },
    solutions: {
      badge: 'Solutions',
      title: 'Our technology understands and measures human attention for real-world impact',
      subtitle:
        'From academic research to automotive safety, our eye tracking solutions power innovation across industries.',
      items: [
        {
          title: 'Research & Insights',
          subtitle: 'Reveal subconscious behavior',
          description:
            'Reveal subconscious behavior with plug-and-play wearables and screen-based trackers.',
          highlights: [
            'Conduct in-depth academic research',
            'Cut training analysis time in half',
            'Increase sales conversion in-store and online',
            'Enhance human-machine interaction studies',
          ],
          ctaLabel: 'Enhance your research',
        },
        {
          title: 'Integrations',
          subtitle: 'Embed world-class eye tracking',
          description:
            "Embed the world's most robust eye tracking into your medical device, PC, glasses or headsets.",
          highlights: [
            'Seamless interaction between users and headsets',
            'Build and deliver top-tier user experiences',
            'High-grade lightweight lenses for next-gen XR',
            "One of the world's largest patent portfolios",
          ],
          ctaLabel: 'Start integrating',
        },
        {
          title: 'Automotive',
          subtitle: 'Safer driving experience',
          description:
            'Enabling a safer, more intuitive and comfortable in-cabin experience — for everyone in the car.',
          highlights: [
            'Driver Monitoring System (DMS)',
            'Occupant Monitoring System (OMS)',
            'Single camera interior sensing',
            'Trusted technology — on the road since 2021',
          ],
          ctaLabel: 'Elevate vehicle intelligence',
        },
        {
          title: 'Gaming',
          subtitle: 'Intensify the experience',
          description:
            'We create technology that helps streamers, e-sports players, and casual gamers intensify their gaming experience.',
          highlights: [
            'Gaze-aware game mechanics',
            'Streaming overlays with eye tracking',
            'Competitive edge for e-sports professionals',
            'Compatible with major gaming platforms',
          ],
          ctaLabel: 'Discover gaming solutions',
        },
      ],
    },
    showcase: {
      videoTitle: 'Revolutionizing technology',
      discoverTitle: 'Discover eye tracking',
      discoverDescription:
        'Understand how gaze reveals attention, intention, and behavior — and why this technology is transforming research, innovation, design, and training.',
      ctaLabel: 'How it works',
      eyeAlt: 'Close-up of a human eye with eye tracking calibration points',
    },
    blog: {
      readMore: 'Read the blog',
      prev: 'Previous blog post',
      next: 'Next blog post',
      dotsLabel: 'Blog slides',
      posts: [
        {
          tag: 'Insight',
          title: 'How eye tracking and visual insights can give F1 drivers a winning edge',
          description:
            'Dr. Zoe Wimshurst works with Formula 1 drivers, examining how they gather and use visual information while operating at the very limits of human performance.',
          author: 'Dr. Zoe Wimshurst',
          readTime: '5 min read',
        },
        {
          tag: 'Research',
          title: 'How to choose the right eye tracker for your research',
          description:
            'From screen-based to wearable systems, learn how to evaluate accuracy, sampling rate, and workflow fit before investing in eye tracking hardware.',
          author: 'Dr. Marisa Bondi',
          readTime: '4 min read',
        },
        {
          tag: 'Healthcare',
          title: 'How eye tracking validates medical device safety and performance',
          description:
            'Discover how gaze data helps teams verify usability, reduce risk, and accelerate regulatory confidence across medical product development.',
          author: 'Pupil Lab',
          readTime: '6 min read',
        },
        {
          tag: 'AI',
          title: 'Seeing smarter: How eye tracking supercharges smart glasses',
          description:
            'With eye tracking, smart glasses know who is wearing them, what they are looking at, and when to respond — bringing natural, hands-free interaction to daily life.',
          author: 'Lawrence Yau',
          readTime: '6 min read',
        },
      ],
    },
    closing: {
      title: 'Start your eye tracking journey today',
      description:
        'Our team of experts is ready to help you explore the possibilities of eye tracking technology for your specific needs.',
      ctaLabel: 'Contact us',
    },
    company: {
      heroTitle: 'Company',
      heroSubtitle:
        'Pupil Lab is the global leader in eye tracking, a position we earned through our passion for technology and our ambition to create tech for a better future.',
      aboutTitle: 'About us',
      aboutDescription:
        "Our journey began more than 20 years ago when we delivered the world's first remote eye tracker. Since then, we've been on a mission to build technology that understands human attention and intent — what we call attention computing.",
      cards: [
        { badge: 'This is Pupil Lab', title: 'This is Pupil Lab', description: 'Find out everything you need to know about Pupil Lab. Discover why we believe in eye tracking and attention computing, and how these technologies will help build a better future.' },
        { badge: 'Global Offices', title: 'Our global offices and hubs', description: 'Our team of people work out of our headquarters in Ho Chi Minh City and locations across Asia, Europe, North America.' },
        { badge: 'Partner Network', title: 'Our partner network', description: 'Our trusted partners around the globe integrate and make use of our technology in their solutions, develop compatible applications in various domains.' },
        { badge: 'Resellers', title: 'Pupil Lab resellers', description: 'We work with an extensive network of resellers around the world. Find the one nearest you.' },
        { badge: 'Company', title: 'Pupil Lab and artificial intelligence', description: 'For over 20 years, Pupil Lab has been at the forefront of eye tracking innovation. Our R&D efforts have driven breakthroughs in machine learning, AI, and advanced sensors.' },
        { badge: 'Certifications', title: 'Certifications', description: 'At Pupil Lab, we pride ourselves on our awarded certifications. We are honored to hold the following ISO certificates: ISO9001, ISO14001 and ISO27001.' },
      ],
      blogTitle: 'Check out our blog',
      blogDescription:
        'Discover how eye tracking and attention computing are used to understand human behavior. We explore the many applications of eye tracking as an assessment and research tool, share expert knowledge, advice and case studies.',
      blogCta: 'The Pupil Lab Blog',
    },
    footer: {
      description:
        'Global leader in eye tracking technology, providing innovative solutions for research, automotive, gaming, and XR integrations.',
      columns: [
        {
          title: 'Solutions',
          links: ['Research & Insights', 'Integrations', 'Automotive', 'Gaming', 'Healthcare'],
        },
        {
          title: 'Technology',
          links: ['Eye Tracking', 'Gaze Estimation', 'Developer SDK', 'Analytics Platform', 'Documentation'],
        },
        {
          title: 'Company',
          links: ['About Us', 'Careers', 'Blog', 'Press', 'Contact'],
        },
        {
          title: 'Resources',
          links: ['Case Studies', 'White Papers', 'Webinars', 'Support', 'Partners'],
        },
      ],
      rights: 'All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      cookies: 'Cookie Settings',
    },
  },
  vi: {
    meta: {
      title: 'Pupil Lab - Công ty hàng đầu về công nghệ theo dõi mắt',
    },
    header: {
      subscribe: 'Đăng ký',
      support: 'Hỗ trợ',
      corporate: 'Doanh nghiệp',
      nav: {
        products: 'Sản phẩm & Giải pháp',
        applications: 'Ứng dụng',
        research: 'Tư vấn nghiên cứu',
        resources: 'Tài nguyên',
        company: 'Công ty',
      },
      contactUs: 'Liên hệ',
      menu: 'Menu',
      closeMenu: 'Đóng menu',
      about: 'Về chúng tôi',
    },
    hero: {
      slides: [
        {
          tag: 'Mới!',
          title: 'Theo dõi mắt qua webcam',
          description:
            'Theo dõi mắt qua webcam giúp nhà nghiên cứu dễ dàng tiếp cận hơn khi chạy các nghiên cứu bằng webcam thông thường. Linh hoạt cho nghiên cứu tại chỗ và từ xa.',
          ctaLabel: 'Tìm hiểu thêm',
        },
        {
          tag: 'Câu chuyện khách hàng',
          title: 'Cải thiện hiệu suất với theo dõi mắt',
          description:
            'Khám phá cách các công ty hàng đầu sử dụng công nghệ theo dõi mắt để nghiên cứu hành vi và cải thiện hiệu suất sản phẩm thông qua khoa học thị giác.',
          ctaLabel: 'Đọc câu chuyện',
        },
        {
          tag: 'Pupil Lab và AI',
          title: 'Theo dõi mắt mang nhận thức ngữ cảnh đến AI',
          description:
            'AI nằm ở trung tâm những gì chúng tôi làm. Công nghệ của chúng tôi chuyển đổi chuyển động đầu và mắt thành insight về hành vi con người thực sự.',
          ctaLabel: 'Khám phá giải pháp AI',
        },
        {
          tag: 'Pupil Lab Insight',
          title: 'Cần hỗ trợ bắt đầu?',
          description:
            'Hợp tác cùng chúng tôi qua dịch vụ nghiên cứu tùy chỉnh, tư vấn chuyên gia hoặc nghiên cứu quy mô lớn — biến dữ liệu theo dõi mắt thành kết quả có ý nghĩa.',
          ctaLabel: 'Nhận tư vấn',
        },
        {
          tag: 'Pupil Lab Blog',
          title: 'Insight thị giác cho ứng dụng thế hệ mới',
          description:
            'Đọc nghiên cứu mới nhất về cách theo dõi mắt và insight thị giác đang thay đổi nhiều ngành từ thể thao đến chẩn đoán y tế.',
          ctaLabel: 'Đọc blog',
        },
      ],
    },
    stats: {
      items: [
        { value: '26', suffix: '', label: 'Năm kinh nghiệm' },
        { value: '95', suffix: '', label: 'Quốc gia trên toàn cầu' },
        { value: '1000', suffix: '+', label: 'Bằng sáng chế & đơn đăng ký' },
        { value: '15000', suffix: '+', label: 'Công bố khoa học' },
        { value: '5', suffix: '', label: 'Headset XR hàng đầu tích hợp theo dõi mắt Pupil Lab' },
        { value: '1', suffix: ' Triệu +', label: 'Xe trên đường sử dụng công nghệ Pupil Lab' },
      ],
    },
    solutions: {
      badge: 'Giải pháp',
      title: 'Công nghệ của chúng tôi hiểu và đo lường sự chú ý của con người cho tác động thực tế',
      subtitle:
        'Từ nghiên cứu học thuật đến an toàn ô tô, giải pháp theo dõi mắt thúc đẩy đổi mới trên nhiều ngành.',
      items: [
        {
          title: 'Nghiên cứu & Insight',
          subtitle: 'Khám phá hành vi tiềm thức',
          description:
            'Thiết bị đeo và theo dõi màn hình plug-and-play cho nghiên cứu học thuật, UX và phân tích hành vi. Khám phá điều thực sự thu hút sự chú ý.',
          highlights: [
            'Thực hiện nghiên cứu học thuật chuyên sâu',
            'Rút ngắn thời gian phân tích đào tạo một nửa',
            'Tăng chuyển đổi bán hàng tại cửa hàng và online',
            'Nâng cao nghiên cứu tương tác người-máy',
          ],
          ctaLabel: 'Nâng tầm nghiên cứu',
        },
        {
          title: 'Tích hợp',
          subtitle: 'Nhúng theo dõi mắt hàng đầu',
          description:
            'Tích hợp nền tảng theo dõi mắt mạnh mẽ nhất vào thiết bị y tế, PC, kính thông minh hoặc headset XR với SDK toàn diện.',
          highlights: [
            'Tương tác liền mạch giữa người dùng và headset',
            'Xây dựng và cung cấp trải nghiệm hàng đầu',
            'Thấu kính nhẹ cao cấp cho thiết bị XR thế hệ mới',
            'Một trong những danh mục bằng sáng chế lớn nhất thế giới',
          ],
          ctaLabel: 'Bắt đầu tích hợp',
        },
        {
          title: 'Ô tô',
          subtitle: 'Trải nghiệm lái xe an toàn hơn',
          description:
            'Mang lại trải nghiệm trong cabin an toàn, trực quan và thoải mái hơn cho mọi người trên xe với hệ thống giám sát tài xế và hành khách.',
          highlights: [
            'Hệ thống giám sát tài xế (DMS)',
            'Hệ thống giám sát hành khách (OMS)',
            'Cảm biến nội thất một camera',
            'Công nghệ đã được kiểm chứng trên đường từ 2021',
          ],
          ctaLabel: 'Nâng tầm trí tuệ xe hơi',
        },
        {
          title: 'Gaming',
          subtitle: 'Nâng cao trải nghiệm chơi game',
          description:
            'Công nghệ giúp streamer, vận động viên e-sports và game thủ nâng tầm trải nghiệm với tương tác và phân tích dựa trên ánh mắt.',
          highlights: [
            'Cơ chế game nhận biết ánh mắt',
            'Overlay streaming với theo dõi mắt',
            'Lợi thế cạnh tranh cho e-sports chuyên nghiệp',
            'Tương thích các nền tảng gaming chính',
          ],
          ctaLabel: 'Khám phá giải pháp gaming',
        },
      ],
    },
    showcase: {
      videoTitle: 'Cách mạng hóa công nghệ',
      discoverTitle: 'Khám phá theo dõi mắt',
      discoverDescription:
        'Hiểu cách ánh mắt phản ánh sự chú ý, ý định và hành vi — và vì sao công nghệ này đang thay đổi nghiên cứu, đổi mới, thiết kế và đào tạo.',
      ctaLabel: 'Cách hoạt động',
      eyeAlt: 'Cận cảnh mắt người với các điểm hiệu chuẩn theo dõi mắt',
    },
    blog: {
      readMore: 'Đọc blog',
      prev: 'Bài blog trước',
      next: 'Bài blog tiếp theo',
      dotsLabel: 'Slide blog',
      posts: [
        {
          tag: 'Insight',
          title: 'Theo dõi mắt và insight thị giác giúp tay đua F1 có lợi thế',
          description:
            'TS. Zoe Wimshurst làm việc với các tay đua F1, nghiên cứu cách họ thu thập và sử dụng thông tin thị giác khi vận hành ở giới hạn hiệu suất con người.',
          author: 'TS. Zoe Wimshurst',
          readTime: '5 phút đọc',
        },
        {
          tag: 'Nghiên cứu',
          title: 'Cách chọn thiết bị theo dõi mắt phù hợp cho nghiên cứu',
          description:
            'Từ hệ thống màn hình đến thiết bị đeo, tìm hiểu cách đánh giá độ chính xác, tần số lấy mẫu và quy trình làm việc trước khi đầu tư.',
          author: 'TS. Marisa Bondi',
          readTime: '4 phút đọc',
        },
        {
          tag: 'Y tế',
          title: 'Theo dõi mắt xác thực an toàn và hiệu năng thiết bị y tế',
          description:
            'Khám phá cách dữ liệu ánh mắt giúp xác minh khả năng sử dụng, giảm rủi ro và tăng tốc niềm tin về quy định trong phát triển sản phẩm y tế.',
          author: 'Pupil Lab',
          readTime: '6 phút đọc',
        },
        {
          tag: 'AI',
          title: 'Nhìn thông minh hơn: Theo dõi mắt nâng tầm kính thông minh',
          description:
            'Với theo dõi mắt, kính thông minh biết ai đang đeo, họ đang nhìn gì và khi nào cần phản hồi — mang tương tác tự nhiên, rảnh tay vào đời sống.',
          author: 'Lawrence Yau',
          readTime: '6 phút đọc',
        },
      ],
    },
    closing: {
      title: 'Bắt đầu hành trình theo dõi mắt ngay hôm nay',
      description:
        'Đội ngũ chuyên gia sẵn sàng giúp bạn khám phá khả năng của công nghệ theo dõi mắt cho nhu cầu cụ thể.',
      ctaLabel: 'Liên hệ',
    },
    company: {
      heroTitle: 'Công ty',
      heroSubtitle:
        'Pupil Lab là công ty hàng đầu thế giới về theo dõi mắt, vị thế đạt được nhờ niềm đam mê công nghệ và tham vọng tạo ra công nghệ cho tương lai tốt đẹp hơn.',
      aboutTitle: 'Về chúng tôi',
      aboutDescription:
        'Hành trình của chúng tôi bắt đầu hơn 20 năm trước khi chúng tôi ra mắt thiết bị theo dõi mắt từ xa đầu tiên trên thế giới. Kể từ đó, chúng tôi đã theo đuổi sứ mệnh xây dựng công nghệ hiểu được sự chú ý và ý định của con người — điều chúng tôi gọi là attention computing.',
      cards: [
        { badge: 'Đây là Pupil Lab', title: 'Đây là Pupil Lab', description: 'Tìm hiểu mọi điều bạn cần biết về Pupil Lab. Khám phá vì sao chúng tôi tin vào theo dõi mắt và attention computing, và cách công nghệ này giúp xây dựng tương lai tốt đẹp hơn.' },
        { badge: 'Văn phòng toàn cầu', title: 'Văn phòng và trung tâm toàn cầu', description: 'Đội ngũ của chúng tôi làm việc tại trụ sở TP. Hồ Chí Minh và các địa điểm trên khắp châu Á, châu Âu, Bắc Mỹ.' },
        { badge: 'Mạng lưới đối tác', title: 'Mạng lưới đối tác', description: 'Các đối tác đáng tin cậy trên toàn cầu tích hợp và sử dụng công nghệ của chúng tôi trong giải pháp của họ, phát triển ứng dụng tương thích trong nhiều lĩnh vực.' },
        { badge: 'Đại lý', title: 'Đại lý Pupil Lab', description: 'Chúng tôi hợp tác với mạng lưới đại lý rộng khắp trên toàn thế giới. Tìm đại lý gần bạn nhất.' },
        { badge: 'Công ty', title: 'Pupil Lab và trí tuệ nhân tạo', description: 'Hơn 20 năm qua, Pupil Lab luôn đi đầu trong đổi mới theo dõi mắt. Nỗ lực R&D đã tạo ra đột phá trong machine learning, AI và cảm biến tiên tiến.' },
        { badge: 'Chứng nhận', title: 'Chứng nhận', description: 'Tại Pupil Lab, chúng tôi tự hào về các chứng nhận đạt được. Chúng tôi vinh dự nắm giữ chứng chỉ ISO: ISO9001, ISO14001 và ISO27001.' },
      ],
      blogTitle: 'Khám phá blog của chúng tôi',
      blogDescription:
        'Khám phá cách theo dõi mắt và attention computing được sử dụng để hiểu hành vi con người. Chúng tôi chia sẻ kiến thức chuyên gia, lời khuyên và case study.',
      blogCta: 'Blog Pupil Lab',
    },
    footer: {
      description:
        'Công ty hàng đầu về công nghệ theo dõi mắt, cung cấp giải pháp đổi mới cho nghiên cứu, ô tô, gaming và tích hợp XR.',
      columns: [
        {
          title: 'Giải pháp',
          links: ['Nghiên cứu & Insight', 'Tích hợp', 'Ô tô', 'Gaming', 'Y tế'],
        },
        {
          title: 'Công nghệ',
          links: ['Theo dõi mắt', 'Ước lượng ánh mắt', 'Developer SDK', 'Nền tảng phân tích', 'Tài liệu'],
        },
        {
          title: 'Công ty',
          links: ['Về chúng tôi', 'Tuyển dụng', 'Blog', 'Báo chí', 'Liên hệ'],
        },
        {
          title: 'Tài nguyên',
          links: ['Case study', 'White paper', 'Webinar', 'Hỗ trợ', 'Đối tác'],
        },
      ],
      rights: 'Bảo lưu mọi quyền.',
      privacy: 'Chính sách bảo mật',
      terms: 'Điều khoản dịch vụ',
      cookies: 'Cài đặt cookie',
    },
  },
};
